import React, { useState, useMemo, PropsWithChildren, useContext, useEffect } from 'react';
import { useRouter } from 'next/router';
import { ActivityMenuList } from './ActivityMenuList';
import { UnitsMenuList } from './UnitsMenuList';
import { useCoursePermissions } from '@components/context/CourseContext';
import { Role } from '@graphql/generated-graphql';
import { ActivityMenuListStudentView } from './ActivityMenuListStudentView';

enum ViewStates {
    UnitsList,
    ActivitiesList,
}

export function CourseUnitsMenu() {
    const { currentState, currentSelectedUnitId } = useCourseUnitsMenuContext();
    const { isCourseRole } = useCoursePermissions();
    if (currentState === ViewStates.UnitsList) {
        return <UnitsMenuList />;
    }

    return isCourseRole(Role.Student) ? (
        <ActivityMenuListStudentView unitId={currentSelectedUnitId} text="" />
    ) : (
        <ActivityMenuList unitId={currentSelectedUnitId} text="" />
    );
}

const CourseMenuProvider = React.createContext<{
    currentState: ViewStates;
    currentSelectedUnitId: string;
} | null>(null);
const CourseMenuDispatcher = React.createContext<{
    setUnit: (unitId: string) => void;
    toggleMenu: () => void;
} | null>(null);

export function CourseUnitContextProvider({ children }: PropsWithChildren<{}>) {
    const { query } = useRouter();
    const [state, setState] = useState({
        currentState: (query?.activityId as string)
            ? ViewStates.ActivitiesList
            : ViewStates.UnitsList,
        currentSelectedUnitId: (query?.unitId as string) ?? '',
    });

    useEffect(() => {
        if (query?.unitId && !state?.currentSelectedUnitId) {
            setState((s) => ({ ...s, currentSelectedUnitId: query.unitId as string }));
        }
    }, [query, state.currentSelectedUnitId, setState]);

    const dispatch = useMemo(
        () => ({
            setUnit: (unitId: string) => {
                setState((s) => ({
                    currentState: ViewStates.ActivitiesList,
                    currentSelectedUnitId: unitId,
                }));
            },
            toggleMenu: () => {
                setState((s) => ({
                    ...s,
                    currentState:
                        s.currentState === ViewStates.UnitsList
                            ? ViewStates.ActivitiesList
                            : ViewStates.UnitsList,
                }));
            },
        }),
        [],
    );

    return (
        <CourseMenuProvider.Provider value={state}>
            <CourseMenuDispatcher.Provider value={dispatch}>
                {children}
            </CourseMenuDispatcher.Provider>
        </CourseMenuProvider.Provider>
    );
}

export function useCourseUnitsMenuContext() {
    const c = useCtx(CourseMenuProvider);
    return c;
}

export function useCourseUnitsMenuDispatcher() {
    const c = useCtx(CourseMenuDispatcher);
    return c;
}

type Cnt<T> = T extends React.Context<infer R> ? NonNullable<R> : never;
export function useCtx<T extends React.Context<any>>(c: T): Cnt<T> {
    const context = useContext(c);
    if (context === null) {
        throw new Error('Expected Context for <Dynamic /> to be Initialized');
    }

    return context;
}
